import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Row, Col, DropdownMenu, DropdownItem } from 'reactstrap';

import useDateLocale from 'services/translations/useDateLocale';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButtonDropdown from 'components/layout/buttons/PrimaryButtonDropdown';
import hasPermissionType from 'services/users/hasPermissionType';
import { WRITE_OFFERS } from 'services/enums/users/permissions';
import IOfferGet from 'api/interfaces/offers/IOfferGet';
import {
  NDA_MASKED_VALUE,
  NDA_STATE_NONE,
  NDA_STATE_RESTRICTED,
} from 'services/enums/offers/ndaProtections';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import { getMarginClass } from 'components/offers/src/offerProduct/calculationWindow/getMarginRatioClassName';

import { fetchOfferTypes, fetchStatusTypes } from 'store/offers/actions';
import BasicMaterialTable from '../layout/tables/BasicMaterialTable';
import DateFilter from '../layout/tables/DateFilter';

import apiOffers from '../../api/offers';
import pdfIcon from '../../assets/pdf_icon.svg';
import OfferStatusOptions from './src/offers/OfferStatusOptions';
import orderOfferStatuses from './src/offers/orderOfferStatuses';
import OfferTypeOptions from './src/offers/OfferTypeOptions';
import orderOfferTypes from './src/offers/orderOfferTypes';
import translations from '../../services/translations/translations';
import {
  BUILDING_TYPE,
  getChoiceLabel,
  useOfferChoices,
} from '../../services/offers/offerChoices';

import 'style/offers/Offers.css';
import 'style/offers/OfferNda.css';
import 'style/offers/MarginRatio.css';
import openOfferPdf from './services/openOfferPdf';
import { fetchUsers } from '../../store/users/actions';
import IUserGet from '../../api/interfaces/users/IUserGet';

// The keys of the departments in the department filter of the list
const DEPARTMENT_KEY_PREFIX = 'department-';

// The columns calculated for each offer, filtered by the server with the same names
const CALCULATED_FILTER_FIELDS = [
  'buildingType',
  'city',
  'marginPercent',
  'orderedAt',
  'postalNumber',
  'projectNumbers',
  'requestedSubmissionDate',
  'revisionNumber',
  'sentAt',
  'streetAddress',
  'totalPrice',
  'workTypes',
];

// The departments read once while the app is open: returning to the list does not wait
// for them again
let cachedDepartments: { id: number; name: string }[] | null = null;

const Offers = ({
  currentUser,
  offerStatusesList,
  fetchOfferStatusesList,
  fetchUsersList,
  offerTypesList,
  usersList,
  fetchOfferTypesList,
}: {
  currentUser: null | IUserGet;
  offerStatusesList: string[];
  fetchOfferStatusesList: () => void;
  fetchUsersList: () => void;
  offerTypesList: string[];
  usersList: IUserGet[];
  fetchOfferTypesList: () => void;
}): JSX.Element | null => {
  const history = useHistory();
  const { t, i18n } = useTranslation([
    translations.offers,
    translations.common,
    translations.projects,
  ]);
  const offerChoices = useOfferChoices();
  const dateLocale = useDateLocale();
  const [sizePerPage, setSizePerPage] = useState(50);
  const [selectedRow, setSelectedRow] = useState<any>();
  // The departments whose offers the user can see, for the department filter
  const [departments, setDepartments] = useState<
    { id: number; name: string }[]
  >(cachedDepartments ?? []);
  // The table reads its default filters once, so it waits for the departments
  const [departmentsLoaded, setDepartmentsLoaded] = useState(
    cachedDepartments !== null
  );
  const OFFERS_FILTER_SESSION = 'offers-filter-local-storage';

  useEffect(() => {
    fetchOfferStatusesList();
    fetchOfferTypesList();
    // The users of the filters are read once; the list blocks the page while it loads
    if (usersList.length === 0) {
      fetchUsersList();
    }
    if (cachedDepartments !== null) {
      return;
    }
    apiOffers.departments
      .get()
      .then((loaded) => {
        cachedDepartments = loaded;
        setDepartments(loaded);
      })
      .catch(() => setDepartments([]))
      .finally(() => setDepartmentsLoaded(true));
  }, []);

  useEffect(() => {
    if (selectedRow !== undefined) {
      history.push(`/offers/${selectedRow?.id}/${selectedRow?.type}`);
    }
  }, [history, selectedRow, selectedRow]);

  const getColumnFieldFromFilter = (query: any, fieldName: string): any => {
    return query.filters?.find((f: any) => f.column.field === fieldName)?.value;
  };

  const getOffers = async (query: any) => {
    try {
      const created = getColumnFieldFromFilter(query, 'created');
      const updated = getColumnFieldFromFilter(query, 'updated');
      const createdUserId = getColumnFieldFromFilter(query, 'createdUserId');
      const updatedUserId = getColumnFieldFromFilter(query, 'updatedUserId');
      const search = getColumnFieldFromFilter(query, 'targetName');
      // Customer and recipient are searched with any part of the name, like the project
      const customer = getColumnFieldFromFilter(query, 'customerName');
      const recipient = getColumnFieldFromFilter(query, 'recipientName');
      const type = getColumnFieldFromFilter(query, 'type');
      const status = getColumnFieldFromFilter(query, 'status');
      const organizationUnitId = getColumnFieldFromFilter(
        query,
        'organizationUnitName'
      )
        ?.map((key: string) => key.replace(DEPARTMENT_KEY_PREFIX, ''))
        .join(',');
      // The calculated columns: texts, ranges (e.g. 10000-50000, <20) and days
      // A choice of several (the building type) goes as a list separated by commas
      const columnFilters = CALCULATED_FILTER_FIELDS.reduce(
        (filters: Record<string, any>, field) => {
          const value = getColumnFieldFromFilter(query, field);
          return {
            ...filters,
            [field]:
              (Array.isArray(value) ? value.join(',') : value) || undefined,
          };
        },
        {}
      );

      // The page and the number of all offers at the same time, not one after the other
      const [list, count] = await Promise.all([
        apiOffers.get({
          ...columnFilters,
          created: created || null,
          createdUserId: createdUserId?.join(','),
          customer,
          organizationUnitId,
          limit: query.pageSize,
          offset: query.page * query.pageSize,
          search,
          orderBy: query.orderBy?.field,
          orderByDirection: query.orderDirection || null,
          recipient,
          updated,
          updatedUserId: updatedUserId?.join(','),
          type: type?.join(','),
          status: status?.join(','),
        }),
        apiOffers.count({
          ...columnFilters,
          created: created || null,
          createdUserId: createdUserId?.join(','),
          customer,
          organizationUnitId,
          search,
          orderBy: query.orderBy?.field,
          orderByDirection: query.orderDirection || null,
          recipient,
          updated,
          updatedUserId: updatedUserId?.join(','),
          type: type?.join(','),
          status: status?.join(','),
        }),
      ]);

      return {
        data: list,
        page: query.page,
        totalCount: count.count,
      };
    } catch (error) {
      return {};
    }
  };

  const colTitles = {
    type: t(`${translations.offers}:Offer Type`),
    customer: t(`${translations.offers}:Customer Office`),
    recipient: t(`${translations.offers}:Recipient`),
    targetName: t(`${translations.offers}:Project`),
    status: t(`${translations.projects}:status.Status`),
    projectNumbers: t(`${translations.offers}:workNumbers.Column`),
    created: t(`${translations.common}:Created`),
    createdBy: t(`${translations.common}:CreatedBy`),
    updated: t(`${translations.common}:Updated`),
    updatedBy: t(`${translations.common}:UpdatedBy`),
    pdf: '',
  };

  const handlePdfOpenEvent = async (e: any, row: any) => {
    e.preventDefault();
    e.stopPropagation();
    return openOfferPdf(row.id, row.type);
  };

  // An opportunity has no offer PDF
  const imageFormatter = (cell: any, row: any) =>
    cell?.type === 'opportunity' ? null : (
      <img
        id={row.targetName}
        alt="pdf icon"
        src={pdfIcon}
        onClick={(e) => handlePdfOpenEvent(e, cell)}
      />
    );

  const typeFormatter = (cell: any, row: any) =>
    t(`${translations.offers}:types:${cell}`);

  usersList.sort((u1, u2) => {
    const n1 = (u1.firstName + u1.lastName).toLowerCase();
    const n2 = (u2.firstName + u2.lastName).toLowerCase();
    return n1.localeCompare(n2);
  });

  const usersFilter: any = {};

  usersList.forEach((u) => {
    const key = u.id ? u.id : '';
    if (u.id == null) {
      return false;
    }
    usersFilter[key] = `${u.firstName} ${u.lastName}`;
    return true;
  });

  // The department filter: key -> name, in the order of the names. The keys are not
  // plain numbers, as the table would order number keys by the number.
  const departmentsFilter: Record<string, string> = {};
  departments.forEach((department) => {
    departmentsFilter[`${DEPARTMENT_KEY_PREFIX}${department.id}`] =
      department.name;
  });

  const getDefaultFilter = (field: string) => {
    const storedFilters = window.localStorage.getItem(OFFERS_FILTER_SESSION);
    if (storedFilters == null) {
      return null;
    }

    const lastUsedFilters = JSON.parse(storedFilters);
    if (lastUsedFilters == null || lastUsedFilters.length <= 0) {
      return null;
    }

    const result = lastUsedFilters.find(
      (lastUsedfilter: any) => lastUsedfilter.column.field === field
    );

    return result != null ? result.value : null;
  };

  // The department of the user is chosen in the list until the user chooses others
  const getDefaultDepartmentFilter = () => {
    const stored = getDefaultFilter('organizationUnitName');
    if (Array.isArray(stored) && stored.length > 0) {
      return stored;
    }

    const ownDepartmentId = currentUser?.organizationUnit?.id;
    return ownDepartmentId !== undefined &&
      departments.some((department) => department.id === ownDepartmentId)
      ? [`${DEPARTMENT_KEY_PREFIX}${ownDepartmentId}`]
      : null;
  };

  // A column of standard content as wide as its content; longer text is cut with "…"
  // Narrow side padding leaves the room to the project
  const CELL_PADDING = '8px 6px';
  // The size of the headings, so standard values fit narrow columns
  const CELL_FONT_SIZE = 14;
  const fixedWidth = (width: number) => ({
    // The width of the column in the table's own layout: the columns without one (the
    // project) share the rest
    width,
    cellStyle: {
      maxWidth: width,
      minWidth: width,
      fontSize: CELL_FONT_SIZE,
      padding: CELL_PADDING,
      whiteSpace: 'nowrap' as const,
      width,
    },
    headerStyle: {
      maxWidth: width,
      minWidth: width,
      padding: CELL_PADDING,
      width,
    },
    // The filters fit the column too
    filterCellStyle: {
      maxWidth: width,
      overflow: 'hidden' as const,
      padding: CELL_PADDING,
      width,
    },
  });

  const ellipsis = (text: null | string | undefined) =>
    text ? (
      <span className="offers-cell-ellipsis" title={text}>
        {text}
      </span>
    ) : null;

  const formatDate = (value: null | string | undefined) =>
    value ? new Date(value).toLocaleDateString(dateLocale.intl) : '';

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  // Today or past, while the offer is not sent yet
  const isSubmissionDue = (row: IOfferGet) => {
    if (!row.requestedSubmissionDate || row.sentAt) {
      return false;
    }

    const date = new Date(row.requestedSubmissionDate);
    date.setHours(0, 0, 0, 0);
    return date.getTime() <= startOfToday.getTime();
  };

  const listTitle = (key: string) => t(`${translations.offers}:list.${key}`);

  // The building types by their names, in the order the admins have given; the hidden ones
  // too, as the offers that have one still show it
  const buildingTypeNames: Record<string, string> = {};
  offerChoices
    .filter((choice) => choice.list === BUILDING_TYPE)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .forEach((choice) => {
      buildingTypeNames[choice.value] = getChoiceLabel(choice, i18n.language);
    });

  // A text typed under the heading, found anywhere in the value; the sums and margins
  // take a range, shown as the placeholder
  const textFilter = (field: string, placeholder?: string) => ({
    defaultFilter: getDefaultFilter(field),
    filterPlaceholder: placeholder,
  });

  // A day chosen from the calendar, as on the created and updated columns
  const dayFilter = (field: string) => ({
    defaultFilter: getDefaultFilter(field),
    filterComponent: DateFilter,
  });

  const columns = [
    {
      // The project takes all the room the other columns leave
      field: 'targetName',
      title: colTitles.targetName,
      defaultFilter: getDefaultFilter('targetName'),
      render: (row: IOfferGet) => (
        <>
          {row.targetName === NDA_MASKED_VALUE
            ? t(`${translations.offers}:nda.Protected`)
            : row.targetName}
          {(row.ndaState ?? NDA_STATE_NONE) !== NDA_STATE_NONE ? (
            <span
              className={`offer-nda-badge ${
                row.ndaState === NDA_STATE_RESTRICTED ? 'restricted' : ''
              }`}
            >
              NDA
            </span>
          ) : null}
        </>
      ),
      // A width of the whole table: the browser gives it what the others leave
      width: '100%',
      cellStyle: {
        fontSize: CELL_FONT_SIZE,
        minWidth: 160,
        padding: CELL_PADDING,
        width: '100%',
      },
      headerStyle: {
        minWidth: 160,
        padding: CELL_PADDING,
        width: '100%',
      },
    },
    {
      field: 'revisionNumber',
      title: t(`${translations.offers}:Revision`),
      ...textFilter('revisionNumber'),
      render: (cell: any) => cell.revisionNumber ?? 1,
      ...fixedWidth(50),
    },
    {
      // The letters of the position codes, e.g. AK, VS
      field: 'workTypes',
      title: listTitle('Work types'),
      // Each work type typed, e.g. "ak, vs"
      ...textFilter('workTypes'),
      render: (row: IOfferGet) => ellipsis(row.workTypes),
      ...fixedWidth(80),
    },
    {
      // The building type of the site, e.g. DC halli; chosen from the types
      field: 'buildingType',
      title: listTitle('Building type'),
      defaultFilter: getDefaultFilter('buildingType'),
      lookup: buildingTypeNames,
      render: (row: IOfferGet) =>
        ellipsis(
          row.buildingType ? buildingTypeNames[row.buildingType] : undefined
        ),
      ...fixedWidth(110),
    },
    // The address of the site in parts, each ordered by itself
    {
      field: 'streetAddress',
      title: listTitle('Street address'),
      ...textFilter('streetAddress'),
      render: (row: IOfferGet) => ellipsis(row.streetAddress),
      ...fixedWidth(135),
    },
    {
      field: 'postalNumber',
      title: listTitle('Postal number'),
      ...textFilter('postalNumber'),
      render: (row: IOfferGet) => row.postalNumber ?? '',
      ...fixedWidth(70),
    },
    {
      field: 'city',
      title: listTitle('City'),
      ...textFilter('city'),
      render: (row: IOfferGet) => ellipsis(row.city),
      ...fixedWidth(100),
    },
    {
      field: 'customerName',
      title: colTitles.customer,
      defaultFilter: getDefaultFilter('customerName'),
      // An offer sent to several companies shows the first one and the number of others
      render: (row: any) => {
        if (
          row.ndaState === NDA_STATE_RESTRICTED &&
          row.customerName === NDA_MASKED_VALUE
        ) {
          return t(`${translations.offers}:nda.Protected`);
        }

        return ellipsis(
          row.customerCount > 1
            ? `${row.customerName ?? ''} (+${row.customerCount - 1})`
            : row.customerName
        );
      },
      ...fixedWidth(230),
    },
    {
      field: 'recipientName',
      title: colTitles.recipient,
      defaultFilter: getDefaultFilter('recipientName'),
      render: (row: IOfferGet) => ellipsis(row.recipientName),
      ...fixedWidth(170),
    },
    {
      // The department of the offer: chosen from the departments, ordered by name
      field: 'organizationUnitName',
      title: t(`${translations.offers}:positionSearch.Organization unit`),
      defaultFilter: getDefaultDepartmentFilter(),
      lookup: departmentsFilter,
      render: (row: any) => ellipsis(row.organizationUnitName),
      ...fixedWidth(110),
    },
    {
      field: 'type',
      title: listTitle('Type'),
      defaultFilter: getDefaultFilter('type'),
      lookup: t(`${translations.offers}:types`, { returnObjects: true }),
      render: (cell: any) => ellipsis(typeFormatter(cell.type, 'type')),
      ...fixedWidth(110),
    },
    {
      field: 'status',
      title: colTitles.status,
      defaultFilter: getDefaultFilter('status'),
      lookup: t(`${translations.offers}:statuses`, { returnObjects: true }),
      // A preliminary offer's "approved" reads "Converted to offer"
      render: (cell: any) =>
        ellipsis(getOfferStatusLabel(t, cell.status, cell.type)),
      ...fixedWidth(95),
    },
    {
      // The D365 project numbers from ProBuilder once ordered (e.g. 000000-00): the
      // main one, the others when the pointer is on it
      field: 'projectNumbers',
      title: colTitles.projectNumbers,
      // Any of the numbers of the offer
      ...textFilter('projectNumbers'),
      render: (row: IOfferGet) => {
        const numbers = (row.projectNumbers ?? '')
          .split(',')
          .map((number) => number.trim())
          .filter((number) => number !== '');

        return numbers.length === 0 ? null : (
          <span className="offers-cell-ellipsis" title={numbers.join('\n')}>
            {numbers[0]}
            {numbers.length > 1 ? (
              <span className="offers-more-numbers">
                {`+${numbers.length - 1}`}
              </span>
            ) : null}
          </span>
        );
      },
      ...fixedWidth(95),
    },
    {
      // The selling price of the primary alternatives
      field: 'totalPrice',
      title: listTitle('Total'),
      ...textFilter('totalPrice', '>10000'),
      render: (row: IOfferGet) =>
        row.totalPrice == null
          ? ''
          : `${row.totalPrice.toLocaleString(dateLocale.intl, {
              maximumFractionDigits: 0,
            })} €`,
      ...fixedWidth(90),
      cellStyle: { ...fixedWidth(90).cellStyle, textAlign: 'right' as const },
    },
    {
      // Marked as on the positions: red below 20 %, green above 30 %
      field: 'marginPercent',
      title: listTitle('Margin'),
      ...textFilter('marginPercent', '<20'),
      render: (row: IOfferGet) =>
        row.marginPercent == null ? null : (
          <span className={`margin-ratio ${getMarginClass(row.marginPercent)}`}>
            {`${row.marginPercent.toLocaleString(dateLocale.intl, {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            })} %`}
          </span>
        ),
      ...fixedWidth(75),
    },
    {
      // Red when the day has come and the offer is not sent yet
      field: 'requestedSubmissionDate',
      title: listTitle('Requested submission'),
      ...dayFilter('requestedSubmissionDate'),
      render: (row: IOfferGet) => (
        <span className={isSubmissionDue(row) ? 'offers-date-due' : undefined}>
          {formatDate(row.requestedSubmissionDate)}
        </span>
      ),
      ...fixedWidth(90),
    },
    {
      field: 'sentAt',
      title: listTitle('Sent'),
      ...dayFilter('sentAt'),
      render: (row: IOfferGet) => formatDate(row.sentAt),
      ...fixedWidth(90),
    },
    {
      field: 'orderedAt',
      title: listTitle('Ordered'),
      ...dayFilter('orderedAt'),
      render: (row: IOfferGet) => formatDate(row.orderedAt),
      ...fixedWidth(85),
    },
    {
      field: 'created',
      title: colTitles.created,
      defaultFilter: getDefaultFilter('created'),
      type: 'date',
      dateSetting: { locale: dateLocale.intl },
      filterComponent: DateFilter,
      ...fixedWidth(90),
    },
    {
      field: 'createdUserId',
      title: colTitles.createdBy,
      defaultFilter: getDefaultFilter('createdUserId'),
      lookup: usersFilter,
      render: (row: IOfferGet) =>
        ellipsis(usersFilter[row.createdUserId ?? '']),
      ...fixedWidth(115),
    },
    {
      field: 'updated',
      title: colTitles.updated,
      defaultFilter: getDefaultFilter('updated'),
      type: 'date',
      dateSetting: { locale: dateLocale.intl },
      filterComponent: DateFilter,
      ...fixedWidth(90),
    },
    {
      field: 'updatedUserId',
      title: colTitles.updatedBy,
      defaultFilter: getDefaultFilter('updatedUserId'),
      lookup: usersFilter,
      render: (row: IOfferGet) =>
        ellipsis(usersFilter[row.updatedUserId ?? '']),
      ...fixedWidth(115),
    },
    {
      field: 'pdf',
      title: colTitles.pdf,
      filtering: false,
      // The link to the PDF has nothing to order by
      sorting: false,
      render: (cell: any) => imageFormatter(cell, 'pdf'),
      ...fixedWidth(36),
    },
  ];

  const getOfferTypes = () => {
    if (offerTypesList.length !== 0) {
      return (
        <DropdownMenu right className="primary-dropdown-menu">
          {orderOfferTypes(offerTypesList).map((type, i) => {
            return (
              <DropdownItem
                onClick={() => history.push(`/offers/types/${i}/create`)}
                key={Math.random()}
              >
                {t(`${translations.offers}:types:${type}`).toUpperCase()}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      );
    }
    return <div />;
  };

  return (
    <PageContainer fullWidth>
      <Row className="row-content">
        <Col xs={9}>
          <p className="header-text">{t(`${translations.common}:Offers`)}</p>
        </Col>
        <Col xs={3} className="header-add-btn">
          {/* With only the right to read, no offers are created */}
          {hasPermissionType(currentUser, WRITE_OFFERS) ? (
            <PrimaryButtonDropdown
              className="add-button"
              text={t(`${translations.common}:Add`)}
            >
              {getOfferTypes()}
            </PrimaryButtonDropdown>
          ) : null}
        </Col>
      </Row>
      <div className="basic-table table-background-offers">
        <Row>
          <Col>
            {departmentsLoaded && currentUser !== null ? (
              <BasicMaterialTable
                columns={columns}
                fetchData={getOffers}
                sizePerPage={sizePerPage}
                setSelectedRow={setSelectedRow}
                localStorageTag={OFFERS_FILTER_SESSION}
              />
            ) : null}
          </Col>
        </Row>
      </div>
    </PageContainer>
  );
};

const mapStateToProps = (state: any) => ({
  offerTypesList: state.offers.offerTypes,
  offerStatusesList: state.offers.statusTypes,
  usersList: state.users.users,
  currentUser: state.users.current,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchOfferTypesList: () => dispatch(fetchOfferTypes()),
  fetchOfferStatusesList: () => dispatch(fetchStatusTypes()),
  fetchUsersList: () => dispatch(fetchUsers()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Offers);
